import { ApiError, QueryParams } from '@/types/api';

// API URL Constants
const API_URLS = {
  local: 'http://localhost:3005/api/v1',
  remote: 'https://ath-avi-be-uat-west.azurewebsites.net/api/v1'
} as const;

// Get API URL from environment or fall back to local
const getApiUrl = (): string => {
  const envUrl = import.meta.env.VITE_API_URL;
  const mode = import.meta.env.MODE;
  
  if (envUrl) {
    return envUrl;
  }
  
  // Fallback based on mode
  if (mode === 'remote') {
    return API_URLS.remote;
  }
  
  return API_URLS.local;
};

const API_BASE_URL = getApiUrl();

export class ApiService {
  private baseURL: string;
  private token: string | null = null;
  
  constructor() {
    this.baseURL = API_BASE_URL;
    this.token = localStorage.getItem('auth-token');
  }

  setToken(token: string | null) {
    this.token = token;
    if (token) {
      localStorage.setItem('auth-token', token);
    } else {
      localStorage.removeItem('auth-token');
    }
  }

  private getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    return headers;
  }

  private buildQueryString(params: QueryParams): string {
    const searchParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (typeof value === 'object') {
          searchParams.append(key, JSON.stringify(value));
        } else {
          searchParams.append(key, value.toString());
        }
      }
    });

    return searchParams.toString();
  }

  private async checkSessionValidity(): Promise<boolean> {
    try {
      const authApiUrl = import.meta.env.VITE_AUTH_API_URL || 'http://localhost:3005/api/v1';
      const response = await fetch(`${authApiUrl}/auth/me`, {
        credentials: 'include'
      });
      return response.ok;
    } catch {
      return false;
    }
  }

  async request<T>(
    endpoint: string,
    options: RequestInit = {},
    timeoutMs: number = 15000, // 15 second default timeout
    isRetry: boolean = false
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;

    const config: RequestInit = {
      headers: this.getHeaders(),
      credentials: 'include', // Send cookies with cross-origin requests
      ...options,
    };

    // Create timeout promise
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`));
      }, timeoutMs);
    });

    try {
      const response = await Promise.race([
        fetch(url, config),
        timeoutPromise
      ]);

      if (!response.ok) {
        // Handle 401 Unauthorized - check if session expired
        if (response.status === 401 && !isRetry) {
          const isSessionValid = await this.checkSessionValidity();

          if (isSessionValid) {
            // Session is valid, retry the original request
            return this.request<T>(endpoint, options, timeoutMs, true);
          } else {
            // Session expired, redirect to login
            window.location.href = '/login';
            throw new Error('Session expired. Redirecting to login...');
          }
        }

        const errorData: ApiError = await response.json();
        throw new Error(errorData.error.message || `HTTP ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('An unknown error occurred');
    }
  }

  async get<T>(endpoint: string, params?: QueryParams, timeoutMs?: number): Promise<T> {
    const queryString = params ? `?${this.buildQueryString(params)}` : '';
    return this.request<T>(`${endpoint}${queryString}`, {
      method: 'GET',
    }, timeoutMs);
  }

  async post<T>(endpoint: string, data?: any, timeoutMs?: number): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    }, timeoutMs);
  }

  async put<T>(endpoint: string, data?: any, timeoutMs?: number): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    }, timeoutMs);
  }

  async delete<T>(endpoint: string, data?: any, timeoutMs?: number): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
      body: data ? JSON.stringify(data) : undefined,
    }, timeoutMs);
  }

  async patch<T>(endpoint: string, data?: any, timeoutMs?: number): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    }, timeoutMs);
  }

  async getBlob(endpoint: string, params?: QueryParams, timeoutMs: number = 15000): Promise<Blob> {
    const queryString = params ? `?${this.buildQueryString(params)}` : '';
    const url = `${this.baseURL}${endpoint}${queryString}`;

    const config: RequestInit = {
      headers: this.getHeaders(),
      method: 'GET',
    };

    // Create timeout promise
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`));
      }, timeoutMs);
    });

    try {
      const response = await Promise.race([
        fetch(url, config),
        timeoutPromise
      ]);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      return await response.blob();
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('An unknown error occurred');
    }
  }

  async uploadFile<T>(
    endpoint: string,
    file: File,
    fieldName: string = 'file',
    additionalData?: Record<string, string>,
    timeoutMs: number = 60000 // 60 second timeout for uploads
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    const formData = new FormData();
    formData.append(fieldName, file);

    // Add any additional form fields
    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }

    // Note: Do NOT set Content-Type header - browser sets it with boundary automatically
    const headers: Record<string, string> = {};
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const config: RequestInit = {
      method: 'POST',
      credentials: 'include',
      headers,
      body: formData
    };

    // Create timeout promise
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Upload timeout after ${timeoutMs / 1000}s`));
      }, timeoutMs);
    });

    try {
      const response = await Promise.race([
        fetch(url, config),
        timeoutPromise
      ]);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: { message: `HTTP ${response.status}` } }));
        throw new Error(errorData.error?.message || `Upload failed: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('An unknown error occurred during upload');
    }
  }
}

export const apiService = new ApiService();